import React, { useState, useEffect, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, Copy, Loader, Loader2 } from "lucide-react";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
import { toast } from "react-hot-toast";

import { useWebhooks, useRetriggerWebhooks } from "../hooks/useWebhooks";
import WebhookDetails from "./WebhookDetails";
import CustomSelect from "../../../components/ui/CustomSelect";

export function WebhookTable() {
  const [providerFilter, setProviderFilter] = useState("");
  const [debouncedProvider, setDebouncedProvider] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const [loadingRowId, setLoadingRowId] = useState(null);

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Success", value: "SUCCESS" },
    { label: "Failed", value: "FAILED" },
    { label: "Pending", value: "PENDING" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProvider(providerFilter);
      if (providerFilter !== debouncedProvider) {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [providerFilter, debouncedProvider]);

  // Build createdAt filter: $btw when both, $gte when only start, $lte when only end
  const createdAtPayload = (() => {
    if (startDate && endDate) return `$btw:${startDate},${endDate}`;
    if (startDate) return `$gte:${startDate}`;
    if (endDate) return `$lte:${endDate}`;
    return undefined;
  })();

  const { data, isLoading, isError, error } = useWebhooks({
    page,
    limit,
    provider: debouncedProvider ? `$ilike:${debouncedProvider}` : undefined,
    status: statusFilter ? `$eq:${statusFilter}` : undefined,
    createdAt: createdAtPayload,
  });
  const { mutate: retriggerWebhook, isPending: isRetriggering } = useRetriggerWebhooks();

  const handleViewDetails = (webhook) => {
    setSelectedWebhook(webhook);
    setIsDetailsOpen(true);
  };

  const handleRetrigger = (webhook) => {
    if (!webhook) return;
    setLoadingRowId(webhook.uuid);
    retriggerWebhook(webhook.uuid, {
      onSuccess: () => {
        toast.success("Webhook retriggered successfully");
        setLoadingRowId(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to retrigger webhook");
        setLoadingRowId(null);
      }
    });
  };

  const truncateText = (text, length = 10) => {
    if (!text) return "";
    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const columns = [
    {
      id: "uuid",
      accessorKey: "uuid",
      header: "Webhook ID",
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>{truncateText(value, 12)}</span>
            <button onClick={() => handleCopy(value)} className="text-gray-500 hover:text-black">
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "provider",
      header: "Provider",
      cell: (info) => (
        <span className="font-semibold text-gray-800 uppercase">{info.getValue() || "N/A"}</span>
      )
    },
    {
      id: "event",
      header: "Event",
      accessorFn: (row) => row.payload?.event || "N/A",
      cell: (info) => (
        <span className="text-sm font-medium text-gray-700">{info.getValue()}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        const statusStyles = {
          success: "bg-green-100 text-green-800",
          failed: "bg-red-100 text-red-800",
          pending: "bg-yellow-100 text-yellow-800",
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyles[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
              }`}
          >
            {status?.toUpperCase() || "UNKNOWN"}
          </span>
        );
      }
    },
    {
      accessorKey: "retriesCount",
      header: "Retries",
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="font-medium text-gray-600">
            {value !== undefined && value !== null ? value : 0}
          </span>
        );
      }
    },
    {
      id: "errorMessage",
      accessorKey: "errorMessage",
      header: "Error Message",
      cell: ({ getValue, row }) => {
        const value = getValue();
        const status = row.original.status;
        return (
          <span className={`block text-xs truncate max-w-[200px] ${status === "FAILED" ? "text-red-500" : "text-gray-500"}`} title={value}>
            {value || "---"}
          </span>
        );
      }
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: "Created At",
      cell: (info) => info.getValue() ? new Date(info.getValue()).toLocaleString() : "N/A"
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const webhook = row.original;
        const isRowLoading = loadingRowId === webhook?.uuid;

        const dropdownItems = [
          {
            label: "View Details",
            onClick: () => handleViewDetails(webhook),
          },
          {
            label: "Retrigger",
            onClick: () => handleRetrigger(webhook),
            className: "text-emerald-600",
          },
        ];

        return (
          <Dropdown
            trigger={
              isRowLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MoreVertical className="w-4 h-4" />
              )
            }
            items={dropdownItems}
            rowIndex={row.index}
          />
        );
      },
    }
  ];

  const records = useMemo(() => data?.data || [], [data]);
  const totalPages = data?.meta?.totalPages || 1;

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    initialState: {
      pagination: {
        pageSize: limit,
        pageIndex: page - 1,
      },
    },
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setProviderFilter(value);
    setPage(1);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Desktop lg+: single row */}
      <div className="hidden gap-4 justify-between items-center pb-6 lg:flex">
        <div className="relative max-w-xs md:max-w-md">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by provider..."
            className="py-3 pr-4 pl-12 w-full max-w-xs text-base rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 md:max-w-md"
            value={providerFilter}
            onChange={handleSearch}
          />
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="date"
            className="px-3 py-2.5 text-sm bg-white rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            title="Start Date"
          />
          <span className="text-xs font-medium text-gray-400">to</span>
          <input
            type="date"
            className="px-3 py-2.5 text-sm bg-white rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            title="End Date"
          />
          <div className="w-44">
            <CustomSelect
              value={statusFilter}
              onChange={handleStatusChange}
              options={statusOptions}
              placeholder="Filter by Status"
            />
          </div>
        </div>
      </div>

      {/* Mobile / md: 2-column grid */}
      <div className="grid grid-cols-2 gap-2 pb-6 lg:hidden">
        <div className="relative col-span-1">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by provider..."
            className="py-3 pr-4 pl-12 w-full text-sm rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={providerFilter}
            onChange={handleSearch}
          />
        </div>
        <div className="col-span-1">
          <CustomSelect
            value={statusFilter}
            onChange={handleStatusChange}
            options={statusOptions}
            placeholder="Filter by Status"
          />
        </div>
        <div className="flex col-span-2 gap-3 items-center mx-auto">
             <div className="">
          <input
            type="date"
            className="w-full px-3 py-2.5 text-sm bg-white rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            title="Start Date"
          />
        </div>
        <div className="flex col-span-2 justify-center items-center">
          <span className="text-xs font-medium text-gray-400">to</span>
        </div>
        <div className="">
          <input
            type="date"
            className="w-full px-3 py-2.5 text-sm bg-white rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            title="End Date"
          />
        </div>
        </div>
     
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading webhooks <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6 ${header.id === "uuid" || header.id === "createdAt" ? "min-w-[120px]" : ""} ${header.id === "errorMessage" ? "min-w-[150px]" : ""}`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100 hover:border-b">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <p className="text-lg font-medium">No webhooks matching your search</p>
                        <p className="text-sm">Try adjusting your filters or search terms</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 justify-between items-center p-3 border-t border-gray-200 md:p-6 bg-gray-50/50 md:flex-row">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(page * limit, data?.meta?.totalItems || 0)}
              </span>{" "}
              of <span className="font-medium">{data?.meta?.totalItems || 0}</span> entries
            </div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <WebhookDetails
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        data={selectedWebhook}
        onRetrigger={handleRetrigger}
        isRetriggering={isRetriggering}
      />
    </div>
  );
}

export default WebhookTable;
